import React, {useEffect, useRef, useCallback, useState, useMemo} from 'react';
import lottie, {AnimationItem} from 'lottie-web';
import {useTranslation} from 'react-i18next';
import {motion} from 'motion/react';
import Svga from '@/controller/svg.controller';
import json from '@assets/gold-coin/data.json';
import img0 from '@assets/gold-coin/images/img_0.png';
import img1 from '@assets/gold-coin/images/img_1.png';
import img2 from '@assets/gold-coin/images/img_2.png';
import img3 from '@assets/gold-coin/images/img_3.png';
import {useSequentialModalContext} from '@basicComponents/sequential-modal/context';

export const MODAL_ID = 'win-modal';

interface WinModalProps {
  visible?: boolean;
  amount?: number;
  reward: string | number;
  rank: number;
}

const WinModal: React.FC<WinModalProps> = ({
  visible = false,
  rank = 0,
  reward = 0,
}) => {
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const {t} = useTranslation();
  const {goToNext} = useSequentialModalContext();
  const closeModal = () => {
    // svgaPlayer.current?.stop();
    // svgaPlayer.current?.dispose();
    // animationRef.current?.destroy();
    goToNext();
  };

  // 修改Lottie文本样式的函数
  const modifyTextStyles = useCallback(
    (
      animationData: any,
      textModifications?: {
        textContent?: string;
        fontSize?: number;
        fontColor?: number[]; // RGB值，范围0-1
        strokeColor?: number[]; // RGB值，范围0-1
        strokeWidth?: number;
      },
      replaceId = '$1',
    ) => {
      if (!textModifications) return animationData;

      if (animationData.assets && Array.isArray(animationData.assets)) {
        animationData.assets.forEach((asset: any) => {
          if (asset.id === 'comp_0' && asset.layers) {
            asset.layers.forEach((layer: any) => {
              // 找到文本图层 (ty: 5) 并匹配 ID
              if (layer.ty === 5 && layer.id === replaceId) {
                const textData = layer.t?.d?.k?.[0]?.s;
                if (textData && textModifications.textContent) {
                  textData.t = textModifications.textContent;
                }
                // 可选：修改字体大小
                if (textData && textModifications.fontSize) {
                  textData.s = textModifications.fontSize;
                }
                // 可选：修改字体颜色
                if (textData && textModifications.fontColor) {
                  textData.fc = textModifications.fontColor;
                }
                // 可选：修改描边颜色
                if (textData && textModifications.strokeColor) {
                  textData.sc = textModifications.strokeColor;
                }
                // 可选：修改描边宽度
                if (textData && textModifications.strokeWidth) {
                  textData.sw = textModifications.strokeWidth;
                }
              }
            });
          }
        });
      }
      return animationData;
    },
    [],
  );

  // 缓存处理后的动画数据
  const processedAnimationData = useMemo(() => {
    const imageMap = {
      'img_0.png': img0,
      'img_1.png': img1,
      'img_2.png': img2,
      'img_3.png': img3,
    };

    // 创建动画数据的副本
    let modifiedData = JSON.parse(JSON.stringify(json));

    // 替换图片资源
    if (modifiedData.assets && Array.isArray(modifiedData.assets)) {
      modifiedData.assets.forEach((asset: any) => {
        if (asset.p && imageMap[asset.p as keyof typeof imageMap]) {
          // 删除相对路径，直接使用导入的图片URL
          delete asset.u;
          asset.p = imageMap[asset.p as keyof typeof imageMap];
        }
      });
    }

    // 修改文本样式
    modifiedData = modifyTextStyles(
      modifiedData,
      {
        textContent: `NO.${rank}`,
      },
      'rank',
    );

    modifiedData = modifyTextStyles(
      modifiedData,
      {
        textContent: `${reward}`,
      },
      'money',
    );

    return modifiedData;
  }, [rank, reward, modifyTextStyles]);
  const svgaCanvas = useRef<HTMLCanvasElement>(null);
  // svga 动画控制器
  const svgaPlayer = useRef<Svga | null>(null);
  const svgaInit = useCallback(() => {
    if (svgaCanvas.current && !svgaPlayer.current) {
      const root = document.getElementById('root');
      const rect = root!.getBoundingClientRect();
      svgaCanvas.current.setAttribute('width', `${rect.width}`);
      svgaCanvas.current.setAttribute('height', `${rect.height}`);
      svgaPlayer.current = new Svga({
        node: svgaCanvas.current,
        dir: '/assets/color-play.svga',
        finish: () => {
          // svgaPlayer.current?.stop();
          svgaPlayer.current?.stop();
          svgaPlayer.current?.play();
        },
      });
    } else {
      svgaPlayer.current?.play();
    }
  }, []);

  // Lottie 动画
  useEffect(() => {
    if (!visible || !lottieContainerRef.current) {
      setIsLoading(false);
      setShowButton(false);
      return;
    }

    const loadAnimation = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        if (!lottieContainerRef.current) return;

        animationRef.current = lottie.loadAnimation({
          container: lottieContainerRef.current,
          renderer: 'svg',
          loop: false,
          autoplay: true,
          animationData: processedAnimationData,
        });
        animationRef.current.setSpeed(0.5);

        // 监听动画加载完成事件
        animationRef.current.addEventListener('DOMLoaded', () => {
          setIsLoading(false);
          setShowButton(true);
          svgaInit();
        });
        // 监听动画错误事件
        animationRef.current.addEventListener('error', () => {
          setHasError(true);
          setIsLoading(false);
        });
        animationRef.current.addEventListener('complete', () => {
          animationRef.current?.goToAndPlay(20, true);
        });
      } catch (error) {
        console.error('加载动画失败:', error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadAnimation();

    return () => {
      if (animationRef.current) {
        animationRef.current.destroy();
        animationRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [visible, processedAnimationData]);

  if (!visible) return null;

  return (
    <div className="relative">
      <canvas
        ref={svgaCanvas}
        className="fixed z-10 left-0 top-0 w-full h-full pointer-events-none"
      />

      {/* 加载状态 */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 z-10">
          <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* 错误状态 */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
          <div className="text-white text-center">
            <div className="text-lg mb-2">Loading Failed</div>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-yellow-400 text-black rounded">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Lottie 动画容器 */}
      <div ref={lottieContainerRef} className="w-full h-full" />

      {/* 底部按钮 */}
      {showButton && (
        <motion.div
          onClick={() => closeModal()}
          className="absolute rounded-full flex items-center justify-center left-1/2 bottom-0 -translate-x-1/2 h-[48px] w-[236px] text-[20px] font-bold text-black"
          style={{
            background:
              'linear-gradient(180deg, #FFF472 0%, #FFF9CF 9.6%, #FFE95E 43.5%, #FFB200 73.72%, #FFDD45 88.46%, #FFCB00 100%)',
            boxShadow:
              '0 0 12px 0 rgba(255, 183, 0, 0.50), -2px 2px 2px 0 rgba(255, 255, 255, 0.70) inset',
          }}
          initial={{scale: 0, opacity: 0, x: '-50%'}}
          animate={{scale: 1, opacity: 1, x: '-50%'}}
          transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20,
            duration: 0.5,
          }}>
          {t('promotionsV2.claimNow')}
        </motion.div>
      )}
    </div>
  );
};
export default WinModal;
